import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class FastRegistrationCreateGuard implements CanActivate {
  private readonly NAV_TOKEN_KEY = 'fast-reg-nav-token';
  private readonly BACKWARD_NAV_KEY = 'fast-reg-backward-nav';

  constructor(private router: Router) {}

  canActivate(): boolean {
    // Check 1: Navigation token validation (must come from start component)
    const navToken = sessionStorage.getItem(this.NAV_TOKEN_KEY);
    if (!navToken || navToken !== 'verified') {
      // Direct access without proper flow - redirect to landing page
      this.redirectToLanding();
      return false;
    }

    // Check 2: Prevent forward navigation after backward navigation
    const hasNavigatedBack = sessionStorage.getItem(this.BACKWARD_NAV_KEY);
    if (hasNavigatedBack === 'true') {
      // User has navigated back - check if this was from auto-submission
      const wasAutoSubmitted = sessionStorage.getItem(
        'fast-reg-auto-submitted'
      );

      this.clearNavigationTokens();

      if (wasAutoSubmitted === 'true') {
        // Auto-submission case - redirect to landing page (bypass start page)
        sessionStorage.removeItem('fast-reg-auto-submitted');
        this.redirectToLanding();
      } else {
        // Manual submission case - redirect to start page for fresh flow
        this.redirectToStart();
      }
      return false;
    }

    // All validations passed - set up backward navigation detection
    this.setupBackwardNavigationDetection();
    return true;
  }

  private redirectToLanding(): void {
    this.router.navigate(['landing']);
  }

  private redirectToStart(): void {
    this.router.navigate(['fast-registration']);
  }

  private clearNavigationTokens(): void {
    sessionStorage.removeItem(this.NAV_TOKEN_KEY);
    sessionStorage.removeItem(this.BACKWARD_NAV_KEY);
    // Note: fast-reg-auto-submitted is cleared separately in the redirect logic above
  }

  private setupBackwardNavigationDetection(): void {
    // Replace current history entry to break back navigation chain
    window.history.replaceState(null, '', window.location.href);

    // Listen for popstate events (back button attempts)
    const handleBackButton = () => {
      // Mark that user attempted backward navigation
      sessionStorage.setItem(this.BACKWARD_NAV_KEY, 'true');

      // Push state again to maintain current position and clear forward history
      window.history.pushState(null, '', window.location.href);
    };

    // Add the event listener
    window.addEventListener('popstate', handleBackButton);

    // Clean up on page unload
    window.addEventListener(
      'beforeunload',
      () => {
        window.removeEventListener('popstate', handleBackButton);
      },
      { once: true }
    );
  }
}
